import React, { memo, useEffect } from 'react';
import Translator from 'translator';
import PropTypes from 'prop-types';
import { Paper, useMediaQuery, Link, Tooltip, CircularProgress } from '@material-ui/core';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import { Search, Close as CloseIcon, Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Save as SaveIcon } from '@material-ui/icons';

import { makeStyles, useTheme } from '@material-ui/styles';
import { FHEReactSelect, FHETextField } from 'data/components';
import { useSetState } from 'data/common';
import {
    SortingState,
    RowDetailState,
    SearchState,
    IntegratedFiltering,
    IntegratedSorting,
    GroupingState,
    IntegratedGrouping,
    PagingState,
    IntegratedPaging,
    DataTypeProvider,
    EditingState
} from '@devexpress/dx-react-grid';
import {
    TableGroupRow,
    TableRowDetail,
    GroupingPanel,
    TableColumnReordering,
    DragDropProvider,
    Toolbar,
    TableEditRow,
    TableEditColumn,
    Grid,
    VirtualTable,
    SearchPanel,
    TableHeaderRow,
    ColumnChooser,
    TableColumnVisibility,
    TableFixedColumns,
    PagingPanel
} from '@devexpress/dx-react-grid-material-ui';
import ToolbarActions from './pluginsTable/ToolbarActions';
import RowActions from './pluginsTable/RowActions';
import RowActionsState from './pluginsTable/RowActionsState';

const localization = {
    groupingPanelMessages: {
        groupByColumn: Translator.translate('general.table.grouping.placeholder')
    },
    searchPanelMessages: {
        searchPlaceholder: Translator.translate('general.table.toolbar.searchPlaceholder')
    },
    columnChooserMessages: {
        showColumnChooser: Translator.translate('general.table.toolbar.addRemoveColumns')
    },
    sortingPanelMessages: {
        sortingHint: Translator.translate('general.table.toolbar.sortTooltip')
    },
    pagingPanelMessages: {
        showAll: Translator.translate('general.table.pagination.showAll'),
        rowsPerPage: Translator.translate('general.table.pagination.labelRowsPerPage'),
        info: Translator.translate('general.table.pagination.labelDisplayedRows')
    }
};

const ActionFormatter = ({ value, row, column, ...restProps }) => {
    if (!row) return value;
    const classes = useStyles(restProps);
    if (column.action && column.action.hasRight && typeof column.action.hasRight === 'function') {
        if (!column.action.hasRight(row)) return value;
    }
    return (
        <Tooltip title={column.action.tooltip} placement="top">
            <div>
                <Link component="button" onClick={column.action.onClick.bind(this, row)} classes={{ root: classes.actionButton }}>
                    {value}
                </Link>
            </div>
        </Tooltip>
    );
};

const RenderFormatter = ({ value, row, column }) => {
    if (typeof column.render !== 'function') return null;
    return column.render(row, column.name, value);
};

const ActionTypeProvider = props => {
    return <DataTypeProvider formatterComponent={ActionFormatter} {...props} />;
};
const RenderTypeProvider = props => {
    return <DataTypeProvider formatterComponent={RenderFormatter} {...props} />;
};

const SelectorEditor = ({ value, onValueChange, column, ...restProps }) => {
    const { selectRender } = column;
    const classes = useStyles(restProps);
    const options = Object.keys(selectRender).reduce((acc, value) => [...acc, { value, label: selectRender[value] }], []);
    return (
        <FHEReactSelect
            className={classes.select}
            name="selector"
            onChange={event => onValueChange(event.value)}
            value={options.find(x => x.value === value)}
            choices={options}
        />
    );
};
const SelectTypeProvider = props => <DataTypeProvider editorComponent={SelectorEditor} {...props} />;

const typeEditor = ({ value, onValueChange, column, ...restProps }) => {
    const { type } = column;
    const onChange = event => {
        let formattedValue = value;
        switch (type) {
            case 'integer':
                formattedValue = parseInt(event.target.value.replace(/\D/g, ''));
                break;
            case 'float':
                formattedValue = parseFloat(event.target.value);
                break;
        }
        onValueChange(formattedValue);
    };
        return <FHETextField value={value} name="type" onChange={onChange} />;
};

const CustomTypeProvider = props => <DataTypeProvider editorComponent={typeEditor} {...props} />;

const TableContainer = props => {
    const { style, ...restProps } = props;
    return (
        <VirtualTable.Container
            {...restProps}
            style={{
                ...style,
                zIndex: 5
            }}
        />
    );
};
const TableContainerMinified = props => {
    const { style, ...restProps } = props;
    return (
        <VirtualTable.Container
            {...restProps}
            style={{
                ...style,
                maxHeight: 400,
                zIndex: 5
            }}
        />
    );
};
const TableCell = props => {
    const { className, ...restProps } = props;
    const classes = useStyles(props);
    return <VirtualTable.Cell {...restProps} className={`${className} ${classes.tableCell}`} />;
};
const TableRowToggleCell = (customToggleCell, props) => {
    if (customToggleCell) return <td>{customToggleCell(props)}</td>;
    return <TableRowDetail.ToggleCell {...props} />;
};

const TableTable = (isLoading, props) => {
    const classes = useStyles(props);
    const { style, className, ...restProps } = props;
    if (isLoading)
        return (
            <VirtualTable.Table {...restProps} style={{ ...style, marginBottom: 'none' }} className={className}>
                <tbody>
                    <tr>
                        <td>
                            <div className={classes.loadingContainer}>
                                <CircularProgress />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </VirtualTable.Table>
        );

    return <VirtualTable.Table {...restProps} style={{ ...style, marginBottom: 'none' }} className={`${className} ${classes.tableStriped}`} />;
};

const SearchPanelInput = ({ onValueChange, value, getMessage, ...restProps }) => {
    return (
        <Input
            onChange={e => onValueChange(e.target.value)}
            value={value}
            type="text"
            placeholder={getMessage('searchPlaceholder')}
            {...restProps}
            startAdornment={
                <InputAdornment position="start">
                    <Search />
                </InputAdornment>
            }
            endAdornment={
                <InputAdornment position="end">
                    <IconButton aria-label="close" size="small" onClick={() => onValueChange('')}>
                        <CloseIcon fontSize="inherit" />
                    </IconButton>
                </InputAdornment>
            }
        />
    );
};
const TableEditColumnCommand = props => {
    const { id, text, onExecute } = props;
    const classes = useStyles(props);
    let Icon = null;
    switch (id) {
        case 'edit':
            Icon = <EditIcon fontSize="inherit" />;
            break;
        case 'commit':
            Icon = <SaveIcon fontSize="inherit" />;
            break;
        case 'cancel':
            Icon = <CloseIcon fontSize="inherit" />;
            break;
        case 'add':
            Icon = <AddIcon fontSize="inherit" />;
            break;
        case 'delete':
            Icon = <DeleteIcon fontSize="inherit" />;
            break;
    }
    return (
        <IconButton aria-label={text} size="small" onClick={onExecute} className={classes.iconButtonCommand}>
            {Icon}
        </IconButton>
    );
};
const HeaderRow = props => {
    const { className, ...restProps } = props;
    const classes = useStyles(props);
    return <TableHeaderRow.Row {...restProps} className={`${className} ${classes.headerRow}`} />;
};
const GridRoot = props => {
    const { className, ...restProps } = props;
    const classes = useStyles(props);
    return <Grid.Root {...restProps} className={`${className} ${classes.gridRoot}`} />;
};
const GroupedCell = props => {
    const { className, ...restProps } = props;
    const classes = useStyles(props);
    return <TableGroupRow.Cell {...restProps} className={`${className} ${classes.groupRowCell}`} />;
};
const FixedCell = props => {
    const { className, ...restProps } = props;
    const classes = useStyles(props);
    return <TableFixedColumns.Cell {...restProps} className={`${className} ${classes.fixedColumnsCell}`} />;
};

const getInitialStateFromcache = currentPage => {
    const tableStateHistory = localStorage.getItem('FHETableStateHistory');
    const FHETableGroupHistory = tableStateHistory ? JSON.parse(tableStateHistory) : {};
    return FHETableGroupHistory[currentPage] ? FHETableGroupHistory[currentPage] : {};
};
// const getExpandedColumns = (columns, data) => {
//     const expandedColumns = columns.filter(column => column.expanded && column.grouped).sort((a, b) => a.grouped - b.grouped);
//     let expandedGroups = [];
//     data.map(aData => {
//         expandedColumns.map((aColumn, index) => {
//             let expandName = '';
//             for (let i = 0; i < index; i++) {
//                 expandName += `${aData[expandedColumns[i].name]}|`;
//             }
//             expandName += aData[aColumn.name];
//             if (expandedGroups.indexOf(expandName) === -1) expandedGroups.push(expandName);
//         });
//     });
//     return expandedGroups;
// };

const saveStateToCache = (path, newState) => {
    const {
        pagesSizes,
        customRenderColumns,
        selectRenderColumns,
        tableColumnExtensions,
        editingStateColumnExtensions,
        leftColumns,
        rightColumns,
        pageSizes,
        actionsColumns,
        integrateFilteringColumnExtensions,
        integratedSortingColumnExtensions,
        ...savingState
    } = newState;
    const tableStateHistory = localStorage.getItem('FHETableStateHistory');
    const FHETableStateHistory = tableStateHistory ? JSON.parse(tableStateHistory) : {};
    FHETableStateHistory[path] = savingState;
    localStorage.setItem('FHETableStateHistory', JSON.stringify(FHETableStateHistory));
};

const FHETable = function(props) {
    const {
        idTable,
        onAddRow,
        onEditRow,
        onDeleteRow,
        title,
        data,
        columns,
        actions,
        pageSize: pageSizeProps,
        pageSizeOptions,
        emptyDataSourceMessage,
        isLoading,
        minified,
        rowDetail
    } = props;
    const classes = useStyles(props);
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('sm'));

    const initTableState = () => {
        const object = {
            expandedRowIds: [],
            currentPage: 0,
            pageSize: minified ? data.length : pageSizeProps,
            pageSizes: pageSizeOptions,
            searchValue: '',
            customRenderColumns: [],
            selectRenderColumns: [],
            typeRenderColumns: [],
            editingStateColumnExtensions: [],
            integratedSortingColumnExtensions: [],
            groupedColumns: [],
            sorting: [],
            tableColumnExtensions: [],
            integrateFilteringColumnExtensions: [],
            expandedGroups: [],
            hiddenColumns: [],
            leftColumns: [],
            rightColumns: [RowActions.ACTION_TYPE],
            columnOrder: [],
            actionsColumns: []
        };
        columns.map(column => {
            if (column.render) object.customRenderColumns.push(column.name);
            if (column.selectRender) object.selectRenderColumns.push(column.name);
            if (column.type) object.typeRenderColumns.push(column.name);
            if (column.unEditable) object.editingStateColumnExtensions.push({ columnName: column.name, editingEnabled: false });
            if (column.customSort) object.integratedSortingColumnExtensions.push({ columnName: column.name, compare: column.customSort });
            if (column.grouped) object.groupedColumns.push({ columnName: column.name });
            if (column.defaultSort) object.sorting.push({ columnName: column.name, direction: column.defaultSort });
            object.tableColumnExtensions.push({ columnName: column.name, wordWrapEnabled: true, width: column.width });
            if (column.customFilter) object.integrateFilteringColumnExtensions.push({ columnName: column.name, predicate: column.customFilter });
            if (column.fixedLeft) object.leftColumns.push(column.name);
            if (column.fixedRight) object.rightColumns.push(column.name);
            if (column.action) object.actionsColumns.push(column.name);
            object.columnOrder.push(column.name);
        });
        object.groupedColumns.sort((a, b) => a.grouped - b.grouped);

        return object;
    };

    const [state, setState] = useSetState(initTableState());

    useEffect(() => {
        const stateFromCache = getInitialStateFromcache(`${window.location.pathname}#${idTable}`);
        setState(stateFromCache);
    }, []);

    useEffect(() => {
        saveStateToCache(`${window.location.pathname}#${idTable}`, state);
    }, [state]);

    const setExpandedRowIds = expandedRowIds => {
        setState({ expandedRowIds });
    };
    const setSearchState = searchValue => {
        setState({ searchValue });
    };
    const setSorting = sorting => {
        setState({ sorting });
    };
    const setCurrentPage = currentPage => {
        setState({ currentPage });
    };
    const setPageSize = pageSize => {
        setState({ pageSize });
    };
    const setGrouping = groupedColumns => {
        setState({ groupedColumns });
    };
    const setExpandedGroups = expandedGroups => {
        setState({ expandedGroups });
    };
    const setHiddenColumns = hiddenColumns => {
        setState({ hiddenColumns });
    };
    const setColumnOrder = columnOrder => {
        setState({ columnOrder });
    };

    const commitChanges = ({ added, changed, deleted }) => {
        if (added) {
            onAddRow(added[0]);
        }
        if (changed) {
            onEditRow(changed[Object.keys(changed)[0]], data[Object.keys(changed)[0]]);
        }
        if (deleted) {
            onDeleteRow(data[deleted[0]]);
        }
    };

    return (
        <Paper className={classes.gridRoot}>
            {!minified && title && <div className={classes.title}>{title} </div>}
            <Grid rows={data} columns={columns} rootComponent={GridRoot}>
                <ActionTypeProvider for={state.actionsColumns} />
                <RenderTypeProvider for={state.customRenderColumns} />
                <SelectTypeProvider for={state.selectRenderColumns} />
                <CustomTypeProvider for={state.typeRenderColumns} />
                {!minified && <DragDropProvider />}

                {!minified && <SearchState value={state.searchValue} onValueChange={setSearchState} />}

                <SortingState sorting={state.sorting} onSortingChange={setSorting} />
                {actions.row.length > 0 && <RowActionsState />}

                <GroupingState
                    grouping={state.groupedColumns}
                    onGroupingChange={setGrouping}
                    expandedGroups={state.expandedGroups}
                    onExpandedGroupsChange={setExpandedGroups}
                />
                {!minified && (
                    <PagingState
                        currentPage={state.currentPage}
                        onCurrentPageChange={setCurrentPage}
                        pageSize={state.pageSize}
                        onPageSizeChange={setPageSize}
                    />
                )}

                {rowDetail !== null && <RowDetailState expandedRowIds={state.expandedRowIds} onExpandedRowIdsChange={setExpandedRowIds} />}
                <IntegratedGrouping />
                {!minified && <IntegratedFiltering columnExtensions={state.integrateFilteringColumnExtensions} />}
                <IntegratedSorting columnExtensions={state.integratedSortingColumnExtensions} />
                {!minified && <IntegratedPaging />}
                {(onAddRow !== null || onEditRow !== null || onDeleteRow !== null) && (
                    <EditingState onCommitChanges={commitChanges} columnExtensions={state.editingStateColumnExtensions} />
                )}
                <VirtualTable
                    height={matches || minified ? 'auto' : 0}
                    columnExtensions={state.tableColumnExtensions}
                    containerComponent={minified ? TableContainerMinified : TableContainer}
                    messages={{
                        noData: emptyDataSourceMessage
                    }}
                    cellComponent={TableCell}
                    tableComponent={TableTable.bind(this, isLoading)}
                />
                {rowDetail !== null && (
                    <TableRowDetail
                        contentComponent={rowDetail.renderPanel}
                        toggleCellComponent={TableRowToggleCell.bind(this, rowDetail.renderToggleCell)}
                    />
                )}
                {!minified && <Toolbar />}
                <TableHeaderRow showSortingControls messages={localization.sortingPanelMessages} rowComponent={HeaderRow} />
                {(onAddRow !== null || onEditRow !== null || onDeleteRow !== null) && <TableEditRow />}
                {(onAddRow !== null || onEditRow !== null || onDeleteRow !== null) && (
                    <TableEditColumn
                        showAddCommand={onAddRow !== null}
                        showEditCommand={onEditRow !== null}
                        showDeleteCommand={onDeleteRow !== null}
                        commandComponent={TableEditColumnCommand}
                    />
                )}
                {!minified && <PagingPanel pageSizes={state.pageSizes} messages={localization.pagingPanelMessages} />}
                {!minified && !matches && (
                    <TableColumnVisibility hiddenColumnNames={state.hiddenColumns} onHiddenColumnNamesChange={setHiddenColumns} />
                )}
                {!matches && <TableColumnReordering order={state.columnOrder} onOrderChange={setColumnOrder} />}
                {!minified && <SearchPanel messages={localization.searchPanelMessages} inputComponent={SearchPanelInput} />}
                {!minified && !matches && <ColumnChooser messages={localization.columnChooserMessages} />}
                {<TableGroupRow cellComponent={GroupedCell} />}
                {!minified && actions.toolbar.length > 0 && <ToolbarActions actions={actions.toolbar} />}
                {actions.row.length > 0 && <RowActions actions={actions.row} />}
                {!matches && <TableFixedColumns leftColumns={state.leftColumns} rightColumns={state.rightColumns} cellComponent={FixedCell} />}
                {!minified && !matches && <GroupingPanel showGroupingControls showSortingControls messages={localization.groupingPanelMessages} />}
            </Grid>
        </Paper>
    );
};

FHETable.propTypes = {
    onAddRow: PropTypes.func,
    onEditRow: PropTypes.func,
    onDeleteRow: PropTypes.func,
    rowDetail: PropTypes.shape({
        renderToggleCell: PropTypes.func,
        renderPanel: PropTypes.func.isRequired
    }),
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
    idTable: PropTypes.string.isRequired,
    title: PropTypes.string,
    actions: PropTypes.shape({
        toolbar: PropTypes.array,
        row: PropTypes.array
    }),
    emptyDataSourceMessage: PropTypes.string,
    pageSize: PropTypes.number,
    pageSizeOptions: PropTypes.array,
    isLoading: PropTypes.bool,
    minified: PropTypes.bool
};

FHETable.defaultProps = {
    actions: { toolbar: [], row: [] },
    onAddRow: null,
    onEditRow: null,
    onDeleteRow: null,
    title: null,
    rowDetail: null,
    isLoading: false,
    minified: false,
    pageSize: 100,
    pageSizeOptions: [10, 50, 100, 0],
    emptyDataSourceMessage: Translator.translate('general.table.body.emptyDataSourceMessage')
};
SearchPanelInput.propTypes = {
    onValueChange: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
    getMessage: PropTypes.func.isRequired
};
RenderFormatter.propTypes = {
    column: PropTypes.object.isRequired,
    row: PropTypes.object,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
RenderFormatter.defaultProps = {
    row: null,
    value: null
};
SelectorEditor.propTypes = {
    column: PropTypes.object.isRequired,
    onValueChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
SelectorEditor.defaultProps = {
    value: null
};
ActionFormatter.propTypes = {
    column: PropTypes.object.isRequired,
    row: PropTypes.object,
    value: PropTypes.string.isRequired
};
ActionFormatter.defaultProps = {
    row: null
};
TableContainer.propTypes = {
    style: PropTypes.object.isRequired
};
TableTable.propTypes = {
    style: PropTypes.object.isRequired,
    className: PropTypes.string
};
TableTable.defaultProps = {
    className: null
};
HeaderRow.propTypes = {
    className: PropTypes.string
};
HeaderRow.defaultProps = {
    className: null
};
GridRoot.propTypes = {
    className: PropTypes.string
};
GridRoot.defaultProps = {
    className: null
};
GroupedCell.propTypes = {
    className: PropTypes.string
};
GroupedCell.defaultProps = {
    className: null
};
FixedCell.propTypes = {
    className: PropTypes.string
};
FixedCell.defaultProps = {
    className: null
};
TableCell.propTypes = {
    className: PropTypes.string
};
TableCell.defaultProps = {
    className: null
};
TableContainerMinified.propTypes = {
    style: PropTypes.object
};
TableContainerMinified.defaultProps = {
    style: {}
};
const useStyles = makeStyles(theme => ({
    actionButton: {
        display: 'contents'
    },
    loadingContainer: {
        left: '50%',
        position: 'fixed',
        marginTop: 20
    },
    title: {
        color: theme.palette.primary.main,
        marginTop: 10,
        marginLeft: 20,
        fontSize: 'x-large'
    },
    tableStriped: {
        '& tbody tr:nth-of-type(2n)': {
            backgroundColor: theme.palette.colors.grey100
        },
        '& tbody tr:nth-of-type(2n+1)': {
            backgroundColor: theme.palette.common.white
        }
    },
    tableCell: {
        overflowWrap: 'break-word'
    },
    groupRowCell: {
        '& :first-child': {
            backgroundColor: 'inherit'
        }
    },
    fixedColumnsCell: {
        backgroundColor: 'inherit'
    },
    headerRow: {
        backgroundColor: theme.palette.common.white
    },
    gridRoot: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%'
    },
    iconButtonCommand: {
        margin: 5
    },
    select: {
        marginTop: 16
    }
}));
export default memo(FHETable);



// WEBPACK FOOTER //
// ./data/components/FHETable.js